import { useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  PackageCheck,
} from "lucide-react";
import { toast } from "sonner";
import {
  useAwaitingReceiptTasks,
  useMarkReceived,
  type CarrierTaskGroup,
} from "@/api/tasks";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { Task } from "@/api.types.v2";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { taskRefs } from "./task-labels";

/**
 * Dispatch-center receiving (lean "A") — "Awaiting Receipt" inbox (Tasks tab).
 * Customer drop-off deliveries (OWNED carrier, no pickup booking) that haven't
 * been physically received at the dispatch center yet (`receivedAt == null`).
 * The planner marks them received once the customer drops them off — only then
 * does the delivery become eligible for the manual backlog. Green/zinc tone:
 * this is routine daily work, not an exception.
 */
export function AwaitingReceiptInbox() {
  const { data, refetch, isLoading, error } = useAwaitingReceiptTasks();

  const groups = data?.data ?? [];
  const total = groups.reduce((n, g) => n + g.tasks.length, 0);

  return (
    <div className="p-3">
      <div className="mb-1 flex items-center gap-2 px-0.5">
        {total === 0 ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <PackageCheck className="h-3.5 w-3.5 text-emerald-600" />
        )}
        <h3 className="text-[11px] font-bold uppercase tracking-wide text-zinc-700">
          Awaiting receipt
        </h3>
        {total > 0 && (
          <span className="ml-auto text-[11px] font-semibold tabular-nums text-emerald-700">
            {total} awaiting drop-off
          </span>
        )}
      </div>
      <p className="mb-3 px-0.5 text-[11.5px] leading-snug text-zinc-500">
        Customer drop-offs not in hand yet. Mark received once the package
        arrives — the delivery then becomes schedulable.
      </p>

      {isLoading && (
        <div className="flex items-center justify-center py-10 text-[12.5px] text-zinc-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading awaiting drop-offs…
        </div>
      )}
      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-600">
          {error instanceof Error ? error.message : "Failed to load"}
        </div>
      )}
      {!isLoading && !error && groups.length === 0 && (
        <div className="py-10 text-center">
          <div className="text-[13px] font-medium text-zinc-700">
            No packages awaiting receipt
          </div>
          <div className="mt-1 text-[11.5px] text-zinc-500">
            Everything dropped off has been received.
          </div>
        </div>
      )}

      <div className="space-y-2">
        {groups.map((g) => (
          <AwaitingGroup
            key={g.carrierCode}
            group={g}
            onReceiveSuccess={() => void refetch()}
          />
        ))}
      </div>
    </div>
  );
}

/** Best-effort customer name from the live shipment relation. */
function customerName(task: Task): string | undefined {
  return task.shipment?.order?.placerCustomer?.name;
}

/**
 * Quoted delivery estimate for an awaiting row. Sourced from the shipment's
 * pre-receipt estimate (`shipment.estimatedDelivery`) — NOT `task.readyAt`,
 * which is null until the package is received and so never renders here. After
 * receipt the server re-anchors the estimate, but for the awaiting panel this
 * is the only estimate available.
 */
function estimateLabel(task: Task): string | undefined {
  const iso = task.shipment?.estimatedDelivery;
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

/** Distinct failure codes, comma-joined, for a partial-failure toast. */
function summarizeFailures(failures: { id: string; error: string }[]): string {
  return Array.from(new Set(failures.map((f) => f.error))).join(", ");
}

function AwaitingGroup({
  group,
  onReceiveSuccess,
}: {
  group: CarrierTaskGroup;
  onReceiveSuccess: () => void;
}) {
  const [open, setOpen] = useState(true);
  const [sel, setSel] = useState<string[]>([]);
  const markReceived = useMarkReceived();
  const { dispatchCenterIds } = useCarrierScope();

  const toggle = (id: string) =>
    setSel((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const receive = (taskIds: string[]) => {
    if (taskIds.length === 0) return toast.error("Select at least one package");
    if (dispatchCenterIds.length !== 1) {
      toast.error("Select one dispatch center before receiving packages");
      return;
    }
    markReceived.mutate(
      { taskIds, dispatchCenterId: dispatchCenterIds[0] },
      {
        onSuccess: (result) => {
          if (result.failed > 0) {
            const reasons = summarizeFailures(result.failures);
            if (result.received > 0) {
              toast.warning("Partially received", {
                description: `Received ${result.received}, ${result.failed} failed (${reasons}).`,
              });
            } else {
              toast.error("Failed to mark received", { description: reasons });
            }
            // Keep the failed packages selected so the planner can retry.
            const failedIds = new Set(result.failures.map((f) => f.id));
            setSel((prev) => prev.filter((id) => failedIds.has(id)));
          } else {
            toast.success("Marked received", {
              description: `${result.received} package(s) received at the dispatch center.`,
            });
            setSel([]);
          }
          onReceiveSuccess();
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error("Failed to mark received", {
            description: e.response?.data?.error ?? "Unknown error",
          });
        },
      }
    );
  };

  return (
    <div
      className="overflow-hidden rounded-xl"
      style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
      >
        <PackageCheck className="h-4 w-4 text-emerald-600" />
        <span className="text-[13px] font-bold text-zinc-900">
          {group.carrierName}
        </span>
        <span className="text-[11px] font-semibold text-emerald-700">
          ({group.tasks.length} awaiting)
        </span>
        <div className="flex-1" />
        {open ? (
          <ChevronDown className="h-4 w-4 text-emerald-700" />
        ) : (
          <ChevronRight className="h-4 w-4 text-emerald-700" />
        )}
      </button>

      {open && (
        <div className="space-y-2 bg-white p-3 pt-2">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSel(group.tasks.map((t) => t.id))}
              className="text-[11.5px] font-semibold text-emerald-700 hover:underline"
            >
              All
            </button>
            <span className="text-zinc-300">·</span>
            <button
              type="button"
              onClick={() => setSel([])}
              disabled={sel.length === 0}
              className="text-[11.5px] font-semibold text-zinc-500 hover:underline disabled:opacity-40"
            >
              Deselect all
            </button>
          </div>

          <div className="-mx-1 max-h-[240px] space-y-0.5 overflow-y-auto">
            {group.tasks.map((task) => {
              const refs = taskRefs(task);
              const who = customerName(task);
              const pkgs = task.shipment?.packages?.length ?? 0;
              const est = estimateLabel(task);
              return (
                <label
                  key={task.id}
                  className="flex cursor-pointer items-start gap-2 rounded p-2 hover:bg-zinc-50"
                >
                  <Checkbox
                    checked={sel.includes(task.id)}
                    onCheckedChange={() => toggle(task.id)}
                    className="mt-0.5"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[12px] font-medium text-zinc-900">
                      {refs.shipmentNumber != null
                        ? `SHP-${refs.shipmentNumber}`
                        : `TSK-${task.taskNumber}`}
                      {refs.orderNumber != null && (
                        <span className="text-zinc-400">
                          {" "}
                          · ORD-{refs.orderNumber}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-zinc-500">
                      {who ?? "Unknown customer"}
                      {pkgs > 0 && (
                        <span>
                          {" · "}
                          {pkgs} pkg{pkgs === 1 ? "" : "s"}
                        </span>
                      )}
                      {est && <span>{` · est. ${est}`}</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      receive([task.id]);
                    }}
                    disabled={markReceived.isPending}
                    className="shrink-0 text-[11px] font-semibold text-emerald-700 hover:underline disabled:opacity-40"
                  >
                    Receive
                  </button>
                </label>
              );
            })}
          </div>

          <div className="flex justify-end pt-1">
            <Button
              size="sm"
              onClick={() => receive(sel)}
              disabled={!sel.length || markReceived.isPending}
              className="h-9 bg-emerald-600 text-[12px] hover:bg-emerald-700"
            >
              {markReceived.isPending
                ? "Marking…"
                : `Mark received${sel.length ? ` (${sel.length})` : ""}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
